/* eslint-disable react/prop-types */
import { useMemo, useState } from 'react';
import {
    Box,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    CardActions,
    Button,
    Chip
} from '@mui/material';
import { IReportsRise } from '../../../../services/api/reports/ReportsServicesRise';
import { Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDateWhitoutHour } from '../../../../services/helpers/helpers';

interface IHistoricRise {
    data: IReportsRise[];
    hasViewPermission?: boolean;
}

export const HistoicRise = ({ data, hasViewPermission = false }: IHistoricRise) => {
    const [filterDate, setFilterDate] = useState<string>('');
    const navigate = useNavigate();
    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));

    const filteredData = useMemo(() => {
        return [...data]
            .filter(report => report.data_inspecao.includes(filterDate))
            .sort((a, b) => {
                const dateA = a.data_inspecao ? new Date(a.data_inspecao).getTime() : 0;
                const dateB = b.data_inspecao ? new Date(b.data_inspecao).getTime() : 0;

                return dateB - dateA;
            });
    }, [data, filterDate]);

    const MobileRiseCard = ({ report }: { report: IReportsRise }) => (
        <Card variant="outlined" sx={{ mb: 2, borderRadius: '15px' }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{ wordBreak: 'break-word', flex: 1, mr: 1, fontSize: smDown ? '1rem' : '1.125rem' }}
                    >
                        Relatório de Inspeção de Segurança
                    </Typography>
                    {report.data_inspecao && (
                        <Chip
                            label={`Ref: ${formatDateWhitoutHour(report.data_inspecao)}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ flexShrink: 0 }}
                        />
                    )}
                </Box>
                {report.data_inspecao && (
                    <Typography
                        variant="body2"
                        component="div"
                        color="text.secondary"
                        sx={{ wordBreak: 'break-word', fontSize: smDown ? '0.8rem' : '0.875rem', mb: report.empresa_executora ? 1 : 0 }}
                    >
                        Data da Inspeção: {formatDateWhitoutHour(report.data_inspecao)}
                    </Typography>
                )}
                {report.empresa_executora && (
                    <Typography
                        variant="body2"
                        component="div"
                        color="text.secondary"
                        sx={{ wordBreak: 'break-word', fontSize: smDown ? '0.8rem' : '0.875rem' }}
                    >
                        Empresa: {report.empresa_executora}
                    </Typography>
                )}
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                <Button
                    startIcon={<Search />}
                    onClick={() => navigate(`./${report.uuid}`)}
                    size="small"
                    variant="outlined"
                    fullWidth
                    disabled={!hasViewPermission}
                >
                    Ver Detalhes
                </Button>
            </CardActions>
        </Card>
    );

    return (
        <Box width={'100%'} p={smDown ? 1 : 2}>
            <Grid container spacing={2} marginBottom={2} alignItems="center">
                <Grid item xs={12} sm={10}>
                    <Typography variant={smDown ? 'h6' : 'h5'} align={smDown ? 'center' : 'left'}>
                        Registro histórico de resumos
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: smDown ? 'center' : 'flex-end' }}>
                    <TextField
                        label="Filtrar por data"
                        type="date"
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth={smDown}
                        sx={{ maxWidth: smDown ? 'none' : 200 }}
                    />
                </Grid>
            </Grid>

            {smDown ? (
                filteredData.length > 0 ? (
                    filteredData.map((report) => (
                        <MobileRiseCard key={report.uuid} report={report} />
                    ))
                ) : (
                    <Paper sx={{ p: 3, textAlign: 'center', borderRadius: '15px' }}>
                        <Typography color="text.secondary">
                            Nenhum resultado encontrado
                        </Typography>
                    </Paper>
                )
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ overflow: 'auto', borderRadius: '15px' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    Data de inspeção
                                </TableCell>
                                <TableCell>
                                    Empresa executora
                                </TableCell>
                                <TableCell>
                                    Ações
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.map((report) => (
                                <TableRow key={report.uuid}>
                                    <TableCell>
                                        {formatDateWhitoutHour(report.data_inspecao)}
                                    </TableCell>
                                    <TableCell>
                                        {report.empresa_executora}
                                    </TableCell>
                                    {hasViewPermission && (
                                        <TableCell>
                                            <IconButton onClick={() => navigate(`./${report.uuid}`)}>
                                                <Search />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {filteredData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        <Typography component="span" color="text.secondary">
                                            Nenhum resultado encontrado
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};